import { Fragment, memo } from 'react';
import { QuotePanelInstance } from '@components/quoteDetails/QuotePanelInstance/LazyQuotePanelInstance';
import { PanelInstance } from '@store/Quote/Quote.types';
import { useQuoteStore } from '@store/Quote/QuoteStore';

type QuotePanelWithChildrenProps = {
  quoteId: string;
  disabled: boolean;
  panel: PanelInstance;
  level: number;

  panelInstancesByParentInstanceId: {
    [parentInstanceId: string]: PanelInstance[];
  };
};

function QuotePanelWithChildrenInner({
  quoteId,
  disabled,
  panel,
  level,
  panelInstancesByParentInstanceId,
}: QuotePanelWithChildrenProps) {
  // Select only the specific visibility flags we need to avoid re-renders when unrelated panel visibilities change
  const isPanelVisible = useQuoteStore((state) => state.panelInstanceVisibilityById[panel.panelInstanceId]);
  const isParentVisible = useQuoteStore((state) =>
    panel.parentPanelInstanceId ? state.panelInstanceVisibilityById[panel.parentPanelInstanceId] : true,
  ); // if no parent, assume it is visible

  // if i have a parent and they are is not visible, remove the panel indent by one level
  let childLevel = isPanelVisible ? level + 1 : Math.max(1, level);
  if (!isParentVisible) {
    childLevel--;
  }

  return (
    <Fragment key={panel.panelInstanceId}>
      <QuotePanelInstance quoteId={quoteId} disabled={disabled} panelInstance={panel} level={level} />
      {panelInstancesByParentInstanceId[panel.panelInstanceId]?.map((childPanel) => (
        <QuotePanelWithChildren
          key={childPanel.panelInstanceId}
          quoteId={quoteId}
          disabled={disabled}
          panel={childPanel}
          panelInstancesByParentInstanceId={panelInstancesByParentInstanceId}
          level={childLevel}
        />
      ))}
    </Fragment>
  );
}

// Memoize to prevent unnecessary re-renders when parent components update but relevant props/visibility have not changed.
// The recursive structure means React.memo must reference the exported identifier afterwards, so we define inner implementation above.
export const QuotePanelWithChildren = memo(QuotePanelWithChildrenInner, (prev, next) => {
  return (
    prev.quoteId === next.quoteId &&
    prev.disabled === next.disabled &&
    prev.level === next.level &&
    prev.panel === next.panel && // rely on stable panel object identity from store
    prev.panelInstancesByParentInstanceId === next.panelInstancesByParentInstanceId
  );
});
